import React, { useMemo, useState } from 'react';
import { Button, IconButton, Modal, MultiSelect, Select, TextInput } from '@carbon/react';
import { TrashCan, Edit } from '@carbon/icons-react';

type GroupRecord = { id: string; name: string; description: string; createdAt: string; updatedAt: string };
type UserRecord = { id: string; name: string; email: string; password: string; groupIds: string[]; role: string };

const seedGroups: GroupRecord[] = [
  { id: 'ops', name: 'Operations', description: 'Daily monitoring and operations group', createdAt: '2024-01-01', updatedAt: '2024-02-15' },
  { id: 'admins', name: 'Administrators', description: 'Full platform access', createdAt: '2023-11-20', updatedAt: '2024-03-10' },
];

const seedUsers: UserRecord[] = [
  { id: 'u1', name: 'Jamie Ortega', email: 'jamie@xdmedia.com', password: "eeee", groupIds: ['ops'], role: 'Operator' },
  { id: 'u2', name: 'Taylor Singh', email: 'taylor@xdmedia.com', password: "123", groupIds: ['admins'], role: 'Admin' },
];

const UserManagementTab: React.FC = () => {
  const [groups, setGroups] = useState<GroupRecord[]>(seedGroups);
  const [users, setUsers] = useState<UserRecord[]>(seedUsers);
  const [groupSearch, setGroupSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ type: 'group' | 'user'; id: string } | null>(null);
  const [groupDraft, setGroupDraft] = useState<GroupRecord>({ id: '', name: '', description: '', createdAt: '', updatedAt: '' });
  const [userDraft, setUserDraft] = useState<UserRecord>({ id: '', name: '', email: '', password: '', groupIds: seedGroups[0] ? [seedGroups[0].id] : [], role: '' });
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  const groupNameLookup = useMemo(
    () => groups.reduce((acc, group) => ({ ...acc, [group.id]: group.name }), {} as Record<string, string>),
    [groups]
  );
  const filteredGroups = useMemo(
    () =>
      groups.filter(
        group =>
          group.name.toLowerCase().includes(groupSearch.toLowerCase()) ||
          group.description.toLowerCase().includes(groupSearch.toLowerCase())
      ),
    [groups, groupSearch]
  );
  const filteredUsers = useMemo(
    () =>
      users.filter(user => {
        const groupNames = user.groupIds.map(id => groupNameLookup[id] || '').join(' ');
        const haystack = `${user.name} ${user.email} ${user.role} ${groupNames}`.toLowerCase();
        return haystack.includes(userSearch.toLowerCase());
      }),
    [users, userSearch, groupNameLookup]
  );

  const openGroupModal = (record?: GroupRecord) => {
    if (record) {
      setEditingGroupId(record.id);
      setGroupDraft({ ...record });
    } else {
      setEditingGroupId(null);
      setGroupDraft({ id: '', name: '', description: '', createdAt: new Date().toISOString().slice(0, 10), updatedAt: new Date().toISOString().slice(0, 10) });
    }
    setGroupModalOpen(true);
  };

  const openUserModal = (record?: UserRecord) => {
    if (record) {
      setEditingUserId(record.id);
      setUserDraft({ ...record, groupIds: [...record.groupIds] });
    } else {
      setEditingUserId(null);
      setUserDraft({ id: '', name: '', email: '', password: '', groupIds: groups[0] ? [groups[0].id] : [], role: 'Operator' });
    }
    setUserModalOpen(true);
  };

  const handleGroupSubmit = () => {
    if (!groupDraft.name.trim()) return;
    if (editingGroupId) {
      setGroups(prev => prev.map(group => (group.id === editingGroupId ? { ...group, ...groupDraft, updatedAt: new Date().toISOString().slice(0, 10) } : group)));
    } else {
      setGroups(prev => [...prev, { ...groupDraft, id: `group-${Date.now()}` }]);
    }
    setGroupModalOpen(false);
  };

  const handleUserSubmit = () => {
    if (!userDraft.name.trim() || !userDraft.email.trim() || userDraft.groupIds.length === 0) return;
    if (editingUserId) {
      setUsers(prev => prev.map(user => (user.id === editingUserId ? { ...user, ...userDraft } : user)));
    } else {
      setUsers(prev => [...prev, { ...userDraft, id: `user-${Date.now()}` }]);
    }
    setUserModalOpen(false);
  };

  const handleDelete = (type: 'group' | 'user', id: string) => setDeleteModal({ type, id });
  const confirmDelete = () => {
    if (!deleteModal) return;
    if (deleteModal.type === 'group') {
      setGroups(prev => prev.filter(group => group.id !== deleteModal.id));
      setUsers(prev => prev.map(user => ({ ...user, groupIds: user.groupIds.filter(id => id !== deleteModal.id) })));
    } else {
      setUsers(prev => prev.filter(user => user.id !== deleteModal.id));
    }
    setDeleteModal(null);
  };

  return (
    <>
      <div className="settings-page__dual-panel">
        <section className="settings-page__list-panel">
          <div className="settings-page__panel-header">
            <div>
              <h3>Groups</h3>
            </div>
            <Button size="sm" kind="primary" onClick={() => openGroupModal()}>
              Add group
            </Button>
          </div>
          <TextInput
            id="group-search"
            labelText=""
            placeholder="Search group name or description"
            value={groupSearch}
            onChange={e => setGroupSearch(e.target.value)}
          />
          <table className="settings-page__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                <th>Edited</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredGroups.length === 0 && (
                <tr>
                  <td colSpan={5} className="settings-page__table-empty">
                    No groups found
                  </td>
                </tr>
              )}
              {filteredGroups.map(group => (
                <tr key={group.id}>
                  <td>{group.name}</td>
                  <td>{group.createdAt}</td>
                  <td>{group.updatedAt}</td>
                  <td className="settings-page__row-actions">
                    <IconButton label="Edit group" kind="ghost" size="sm" onClick={() => openGroupModal(group)}>
                      <Edit />
                    </IconButton>
                    <IconButton label="Delete group" kind="ghost" size="sm" onClick={() => handleDelete('group', group.id)}>
                      <TrashCan />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="settings-page__list-panel settings-page__list-panel--primary">
          <div className="settings-page__panel-header">
            <div>
              <h3>Users</h3>
            </div>
            <Button size="sm" kind="primary" onClick={() => openUserModal()}>
              Add user
            </Button>
          </div>
          <TextInput
            id="user-search"
            labelText=""
            placeholder="Search name, email or role"
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
          />
          <table className="settings-page__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Group</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={5} className="settings-page__table-empty">
                    No users found
                  </td>
                </tr>
              )}
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>
                    {user.groupIds.length === 0 && '—'}
                    {user.groupIds.map(groupId => (
                      <span key={groupId} className="settings-page__chip">
                        {groupNameLookup[groupId] || '—'}
                      </span>
                    ))}
                  </td>
                  <td className="settings-page__row-actions">
                    <IconButton label="Edit user" kind="ghost" size="sm" onClick={() => openUserModal(user)}>
                      <Edit />
                    </IconButton>
                    <IconButton label="Delete user" kind="ghost" size="sm" onClick={() => handleDelete('user', user.id)}>
                      <TrashCan />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <Modal
        open={groupModalOpen}
        modalHeading={editingGroupId ? 'Edit group' : 'Add group'}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestClose={() => setGroupModalOpen(false)}
        onRequestSubmit={handleGroupSubmit}
      >
        <div className="settings-page__form-grid">
          <TextInput
            id="group-name-field"
            labelText="Group name"
            value={groupDraft.name}
            onChange={e => setGroupDraft({ ...groupDraft, name: e.target.value })}
          />
          <TextInput
            id="group-description-field"
            labelText="Description"
            value={groupDraft.description}
            onChange={e => setGroupDraft({ ...groupDraft, description: e.target.value })}
          />
        </div>
      </Modal>

      <Modal
        open={userModalOpen}
        modalHeading={editingUserId ? 'Edit user' : 'Add user'}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestClose={() => setUserModalOpen(false)}
        onRequestSubmit={handleUserSubmit}
      >
        <div className="settings-page__form-grid">
          <TextInput
            id="user-name-field"
            labelText="Full name"
            value={userDraft.name}
            onChange={e => setUserDraft({ ...userDraft, name: e.target.value })}
          />
          <TextInput
            id="email-field"
            labelText="Email"
            type="email"
            value={userDraft.email}
            onChange={e => setUserDraft({ ...userDraft, email: e.target.value })}
          />
          <TextInput
            id="user-password-field"
            labelText="Password"
            type="password"
            value={userDraft.password}
            onChange={e => setUserDraft({ ...userDraft, password: e.target.value })}
          />
          <Select
            id="user-role-field"
            labelText="Role"
            value={userDraft.role}
            onChange={e => setUserDraft({ ...userDraft, role: e.target.value })}
          >
            <option value="Admin">Admin</option>
            <option value="Operator">Operator</option>
            <option value="Viewer">Viewer</option>
          </Select>
          <MultiSelect
            id="user-groups-field"
            titleText="Groups"
            disabled={!groups.length}
            label="Select groups"
            items={groups}
            itemToString={item => (item ? item.name : '')}
            selectedItems={groups.filter(group => userDraft.groupIds.includes(group.id))}
            onChange={({ selectedItems }) =>
              setUserDraft({ ...userDraft, groupIds: selectedItems.map(item => item.id) })
            }
          />
        </div>
      </Modal>

      <Modal
        open={!!deleteModal}
        danger
        modalHeading="Delete record"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteModal(null)}
        onRequestSubmit={confirmDelete}
      >
        <p>
          Are you sure you want to delete this {deleteModal?.type === 'group' ? 'group' : 'user'}? This action cannot be undone.
        </p>
      </Modal>
    </>
  );
};

export default UserManagementTab;
